import React, { useCallback, useReducer, useState } from 'react';
import { TableFormat } from 'roosterjs-editor-types';
import styled from 'styled-components';
import { FullWidthStackPanel } from './common';
import { useTableFormat } from './table-format-store';
import { Typography } from '../../../../../typography';
import { StackPanel } from '../../../../../stack-panel';
import { ColorPickerCallout } from '../../../../../color-picker-callout';
import { PrimaryButton } from '../../../../../button';
import { IBaseComponentProps } from '../../../../../types';

interface IColorSelectionAction {
    key: keyof TableFormat;
    color: string;
}

interface IColoreTileProps extends IBaseComponentProps {
    defaultColor?: string | null;
    label: string;
    formatPropName: keyof TableFormat;
    onChange(selectionInfo: IColorSelectionAction): void;
}

function ColorTileBase(props: IColoreTileProps) {
    const { className, label, formatPropName, defaultColor, onChange } = props;
    const [color, setColor] = useState(defaultColor);

    const _onChange = useCallback((selectedColor: string) => {
        setColor(selectedColor);
        onChange({ key: formatPropName, color: selectedColor });
    }, [formatPropName, onChange]);

    return (
        <StackPanel className={className} orientation="vertical" title={color || undefined} alignment="center">
            <ColorPickerCallout pickerTargetElementHeight={28} pickerTargetElementWidth={28}
                pickerSurfaceHeight={300} pickerSurfaceWidth={300}
                defaultColor={defaultColor || undefined} onColorChange={_onChange} />
            <Typography.H6>{label}</Typography.H6>
        </StackPanel>
    );
}

const ColorTile = styled(ColorTileBase)`
    width: 100px;
    
    .ap-color-picker-callout:first-child {
        border-top: #D8DADD 1px solid;
    }
`;

interface ITableColorsConfigProps {
    onApply(): void;
}

function colorSelectionReducer(state: TableFormat, action: IColorSelectionAction) {
    return { ...state, ...{ [action.key]: action.color } };
}

export function TableColorsConfig(props: ITableColorsConfigProps) {
    const { onApply } = props;
    const { setTableFormat, getTableFormat } = useTableFormat();
    const [colorSelectionInfo, dispatch] = useReducer(colorSelectionReducer, getTableFormat());

    const applyColors = useCallback(() => {
        setTableFormat(colorSelectionInfo);
        onApply();
    }, [colorSelectionInfo, onApply, setTableFormat]);

    return (
        <FullWidthStackPanel orientation="vertical" gap="12">
            <FullWidthStackPanel orientation="horizontal" gap="12">
                <ColorTile key="even" label="Even Row" defaultColor={colorSelectionInfo.bgColorEven} formatPropName="bgColorEven" onChange={dispatch} />
                <ColorTile key="odd" label="Odd Row" defaultColor={colorSelectionInfo.bgColorOdd} formatPropName="bgColorOdd" onChange={dispatch} />
            </FullWidthStackPanel>
            <FullWidthStackPanel orientation="horizontal" gap="12">
                <ColorTile key="top" label="Top Border" defaultColor={colorSelectionInfo.topBorderColor} formatPropName="topBorderColor" onChange={dispatch} />
                <ColorTile key="bottom" label="Bottom Border" defaultColor={colorSelectionInfo.bottomBorderColor} formatPropName="bottomBorderColor" onChange={dispatch} />
                <ColorTile key="vertical" label="Vertical Border" defaultColor={colorSelectionInfo.verticalBorderColor} formatPropName="verticalBorderColor" onChange={dispatch} />
            </FullWidthStackPanel>
            <FullWidthStackPanel orientation="horizontal" justifyContent="center">
                <PrimaryButton size="small" label="Apply Format" onClick={applyColors} />
            </FullWidthStackPanel>
        </FullWidthStackPanel>
    );
}
